import { isNullOrUndefined } from '@cs/core';

export class DateUtils {
	static splitCfDate(idDate): IConvertedDay {
		const intern = idDate.toString();
		let out: IConvertedDay;

		if (intern.length === 6) {
			// 201604
			out = {
				month: parseInt(intern.substr(intern.length - 2), 10),
				year:  parseInt(intern.substr(0, 4), 10),
				day:   0
			};
		} else if (intern.length === 8) {
			// 20160423
			out = {
				month: parseInt(intern.substr(4, 2), 10),
				year:  parseInt(intern.substr(0, 4), 10),
				day:   parseInt(intern.substr(intern.length - 2), 10)
			};
		}
		return out;
	}

	static convertCfDateToJsDate(date): Date {
		if (date === '')
			return null;

		const cd = this.splitCfDate(date);
		let jsDate: Date;
		if (!isNullOrUndefined(cd)) {
			jsDate = new Date();
			jsDate.setFullYear(cd.year, cd.month - 1, cd.day);
		} else {
			jsDate = new Date(date);
		}
		return jsDate;
	}

	static convertJsDateToCfDate(date: Date, withDay = false): number {
		let day     = '';
		const month = date.getMonth() + 1; // JS 0 index month
		const year  = date.getFullYear();

		let field = month.toString();
		if (field.length === 1) {
			field = 0 + field;
		}

		if (withDay) {
			day = date.getDate().toString();
			if (day.length === 1) {
				day = 0 + day;
			}
		}

		return parseInt(year + field + day, 10);
	}

	static addDays(date: Date, days: number): Date {
		const copy = new Date(Number(date));
		copy.setDate(date.getDate() + days);
		return copy;
	}

	/**
	 * Checks if a date value is in Cohelion integer format (e.g., 20021228) or can be parsed as such
	 * @param value - The value to check
	 * @returns true if it's a Cohelion date format (6 or 8 digit integer), false if it's an ISO date string or invalid
	 */
	static isCohelionDateFormat(value: any): boolean {
		if (isNullOrUndefined(value) || value === '') {
			return false;
		}

		// Check if value is already a number
		if (typeof value === 'number') {
			const str = value.toString();
			return str.length === 6 || str.length === 8; // YYYYMM or YYYYMMDD
		}

		// Check if value is a string that can be parsed to integer
		if (typeof value === 'string') {
			// First check if it looks like an ISO date (contains hyphens, colons, or T)
			if (value.includes('-') || value.includes('T') || value.includes(':')) {
				return false; // This is likely an ISO date string
			}

			// Try to parse as integer
			const parsed = parseInt(value, 10);
			if (!isNaN(parsed) && parsed.toString() === value.trim()) {
				const str = parsed.toString();
				return str.length === 6 || str.length === 8; // YYYYMM or YYYYMMDD
			}
		}

		return false;
	}

	/**
	 * Determines the type of date format for the given value
	 * @param value - The value to analyze
	 * @returns 'integer' for Cohelion format, 'iso' for ISO date strings, 'invalid' for unparseable values
	 */
	static getDateType(value: any): 'integer' | 'iso' | 'invalid' {
		if (isNullOrUndefined(value) || value === '') {
			return 'invalid';
		}

		// Check if it's a Cohelion integer date format
		if (this.isCohelionDateFormat(value)) {
			return 'integer';
		}

		// Check if it's a valid ISO date string
		if (typeof value === 'string') {
			const d = new Date(value);
			if (!isNaN(d.getTime())) {
				return 'iso';
			}
		}

		// Check if it's already a Date object
		if (value instanceof Date && !isNaN(value.getTime())) {
			return 'iso';
		}

		return 'invalid';
	}

}

export interface IConvertedDay {
	year: number;
	month: number;
	day?: number;
}
